import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Loader2, Shield, ShieldCheck, ShieldOff, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/harikaradmin/admins")({
  head: () => ({ meta: [{ title: "Adminler — JUËN Admin" }] }),
  component: AdminsPage,
});

type AdminRow = {
  user_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  is_superadmin: boolean;
};

type ProfileRow = {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
};

function AdminsPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [fetching, setFetching] = useState(true);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<ProfileRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
    if (!loading && user && !isAdmin) void navigate({ to: "/dashboard" });
  }, [loading, user, isAdmin, navigate]);

  const refresh = async () => {
    setFetching(true);
    const { data, error } = await supabase.rpc("list_admins");
    if (error) toast.error(error.message);
    else setAdmins((data as AdminRow[]) ?? []);
    setFetching(false);
  };

  useEffect(() => {
    if (isAdmin) void refresh();
  }, [isAdmin]);

  const adminIds = useMemo(() => new Set(admins.map((a) => a.user_id)), [admins]);

  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name")
        .or(`email.ilike.%${term}%,first_name.ilike.%${term}%,last_name.ilike.%${term}%`)
        .limit(20);
      if (cancelled) return;
      if (error) toast.error(error.message);
      else setResults((data as ProfileRow[]) ?? []);
      setSearching(false);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [search]);

  const grant = async (uid: string, name: string) => {
    setBusy(uid);
    const { error } = await supabase.rpc("admin_grant_admin", { _user_id: uid });
    setBusy(null);
    if (error) return toast.error(error.message);
    toast.success(`${name} admin yapıldı`);
    setSearch("");
    setResults([]);
    void refresh();
  };

  const revoke = async (uid: string, name: string) => {
    if (!confirm(`${name} adlı kullanıcının admin yetkisini kaldır?`)) return;
    setBusy(uid);
    const { error } = await supabase.rpc("admin_revoke_admin", { _user_id: uid });
    setBusy(null);
    if (error) return toast.error(error.message);
    toast.success("Admin yetkisi kaldırıldı");
    void refresh();
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Link to="/harikaradmin" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Admin
        </Link>

        <h1 className="text-3xl font-bold tracking-tight">Adminler</h1>
        <p className="mt-2 text-muted-foreground">Üyeleri arayıp admin yapabilir veya mevcut adminlerin yetkisini kaldırabilirsin.</p>

        <div className="mt-6">
          <AdminTabs />
        </div>

        <div className="mt-2 rounded-2xl border border-border bg-card p-5 shadow-card">
          <h2 className="font-semibold">Üye ara ve admin yap</h2>
          <Input
            className="mt-3"
            placeholder="Ad, soyad veya e-posta..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {searching && <div className="mt-3 text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Aranıyor...</div>}
          {results.length > 0 && (
            <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
              {results.map((r) => {
                const name = `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim() || r.email || "—";
                const isCurrentAdmin = adminIds.has(r.id);
                return (
                  <li key={r.id} className="flex items-center justify-between gap-3 p-3">
                    <div className="min-w-0">
                      <div className="truncate font-medium">{name}</div>
                      <div className="truncate text-xs text-muted-foreground">{r.email}</div>
                    </div>
                    {isCurrentAdmin ? (
                      <span className="text-xs text-muted-foreground">Zaten admin</span>
                    ) : (
                      <Button size="sm" disabled={busy === r.id} onClick={() => void grant(r.id, name)}>
                        {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="mr-1.5 h-4 w-4" />}
                        Admin yap
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="mt-6 rounded-2xl border border-border bg-card shadow-card">
          <div className="flex items-center gap-2 border-b border-border p-5">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h2 className="font-semibold">Mevcut adminler</h2>
          </div>
          {fetching ? (
            <div className="p-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : admins.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Admin yok.</div>
          ) : (
            <ul className="divide-y divide-border">
              {admins.map((a) => {
                const name = `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim() || a.email || "—";
                const isSelf = a.user_id === user.id;
                return (
                  <li key={a.user_id} className="flex items-center justify-between gap-3 p-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium">{name}</span>
                        {isSelf && <span className="text-xs text-muted-foreground">(sen)</span>}
                        {a.is_superadmin ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                            <ShieldCheck className="h-3 w-3" /> Superadmin
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            <Shield className="h-3 w-3" /> Admin
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">{a.email}</div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isSelf || busy === a.user_id}
                      onClick={() => void revoke(a.user_id, name)}
                    >
                      {busy === a.user_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4 text-destructive" />}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
